import { useEffect, useState } from "react";
import { useTranslation } from "../context/TranslationContext";
import { updateAppSettings } from "../api/mintSpace";
import { getInstanceDisplayName, getInstanceVisibility } from "../utils/mintSpaceApps";
import AppIcon from "./ds/AppIcon";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import Modal from "./ds/Modal";
import TextField from "./ds/TextField";

/**
 * Rename + public/private settings for a single installed app instance.
 */
export default function MintSpaceAppPropertiesModal({
  isOpen,
  onClose,
  isDark,
  mintSpace,
  catalogApp,
  instance,
  setToast,
  onSaved,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !catalogApp || !instance) return;
    setName(getInstanceDisplayName(mintSpace, instance, catalogApp.name || ""));
    setVisibility(getInstanceVisibility(mintSpace, instance));
  }, [isOpen, catalogApp, instance, mintSpace]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!mintSpace?._id || !catalogApp || !instance?.instanceId) return;
    setSaving(true);
    try {
      const result = await updateAppSettings(mintSpace._id, {
        instanceId: instance.instanceId,
        appId: catalogApp.id,
        name: name.trim(),
        visibility,
      });
      if (result.success) {
        setToast?.(t("mintSpace.appPropertiesSaved") || "App properties saved");
        onSaved?.(result.data);
        onClose?.();
      }
    } catch (err) {
      setToast?.(
        err.message || t("mintSpace.appPropertiesFailed") || "Could not save properties"
      );
    } finally {
      setSaving(false);
    }
  };

  const option = (value, icon, label, hint) => {
    const active = visibility === value;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => setVisibility(value)}
        className={`flex flex-1 items-start gap-space-8 rounded-[22px] bg-bg-inset p-space-12 text-left ${
          active ? "shadow-[inset_0_0_0_1px_var(--color-accent-default)]" : "hover:bg-fill-quaternary"
        }`}
      >
        <Icon name={icon} size={16} className={`mt-[2px] ${active ? "text-accent-text" : "text-label-secondary"}`} />
        <span className="flex min-w-0 flex-col gap-[2px]">
          <span className="text-footnote-semibold text-label-primary">{label}</span>
          <span className="text-caption-1 text-label-secondary">{hint}</span>
        </span>
      </button>
    );
  };

  return (
    <Modal
      open={Boolean(isOpen && catalogApp && instance)}
      onClose={() => !saving && onClose?.()}
      title={t("mintSpace.appPropertiesTitle") || "App properties"}
      description={catalogApp?.name}
      closeLabel={t("common.close") || "Close"}
      isDark={isDark}
      media={catalogApp ? <AppIcon app={catalogApp.id} size={44} /> : null}
    >
      <form onSubmit={handleSave} className="flex flex-col gap-space-20">
        <TextField
          label={t("mintSpace.appDisplayName") || "Display name"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder={catalogApp?.name}
        />

        <div className="flex flex-col gap-[6px]">
          <p className="text-footnote-semibold text-label-secondary">{t("mintSpace.appVisibility") || "Visibility"}</p>
          <div className="flex flex-col gap-space-8 min-[600px]:flex-row">
            {option(
              "public",
              "users",
              t("mintSpace.visibilityPublic") || "Public",
              t("mintSpace.appVisibilityPublicHint") || "Everyone in this Mint Space can see this app."
            )}
            {option(
              "private",
              "lock",
              t("mintSpace.visibilityPrivate") || "Private",
              t("mintSpace.appVisibilityPrivateHint") || "Only admins of this Mint Space can see this app."
            )}
          </div>
        </div>

        <div className="flex justify-end gap-space-12">
          <Button variant="gray" disabled={saving} onClick={onClose}>
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button type="submit" loading={saving}>
            {saving ? t("common.saving") || "Saving…" : t("common.save") || "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
